export type GlossaryRow = {
  id: string;
  metric: string;
  meaning: string;
};

export type GlossaryReportKey = 'general' | 'waste' | 'carbon';

export type GlossarySection = {
  reportKey: GlossaryReportKey;
  sectionKey: string;
  /** English catalog title: "{report} — {subsection}" (stable for filtering). */
  sectionTitle: string;
  rows: GlossaryRow[];
};

/** Metric definitions tab: one row per metric or column; plain language, no “API”. */
export const REPORT_METRIC_GLOSSARY_SECTIONS: GlossarySection[] = [
  {
    reportKey: 'general',
    sectionKey: 'general-exec-summary',
    sectionTitle: 'General ESG Performance Report — Executive summary',
    rows: [
      {
        id: 'gen-exec-total-waste-handled',
        metric: 'Total waste handled (platform-recorded incoming)',
        meaning:
          'Sum of incoming waste amounts recorded for this month when a breakdown exists; may not equal total enterprise waste.',
      },
      {
        id: 'gen-exec-diversion-rate',
        metric: 'Waste diversion rate',
        meaning:
          'Share of waste shown as diverted from disposal under Recyclee ESG rules for your role and period.',
      },
      {
        id: 'gen-exec-total-recycled',
        metric: 'Total waste recycled',
        meaning:
          'Mass recycled for your organization in the selected month as stored in your dashboard totals.',
      },
      {
        id: 'gen-exec-carbon-avoided',
        metric: 'Total carbon avoided',
        meaning:
          'Estimated tonnes of CO2 equivalent avoided through recycling activity recorded this month.',
      },
      {
        id: 'gen-exec-utility-cost',
        metric: 'Total utility cost',
        meaning:
          'Combined SAR spend shown for water, electricity, oil, and gas utilities this period.',
      },
      {
        id: 'gen-exec-training-hours',
        metric: 'Total training hours',
        meaning:
          'Training hours summarized for the month when available; details remain under Employee Training.',
      },
    ],
  },
  {
    reportKey: 'general',
    sectionKey: 'general-waste-metrics',
    sectionTitle: 'General ESG Performance Report — Waste metrics',
    rows: [
      {
        id: 'gen-waste-diverted',
        metric: 'Total waste diverted',
        meaning:
          'Mass diverted from disposal paths according to your dashboard figures for the month.',
      },
      {
        id: 'gen-waste-diversion-detail',
        metric: 'Waste diversion rate (detail)',
        meaning:
          'Same diversion percentage echoed beside diverted mass for quick comparison.',
      },
      {
        id: 'gen-waste-recycled-detail',
        metric: 'Total waste recycled (detail)',
        meaning:
          'Recycled mass repeated here alongside diversion metrics on the waste metrics page.',
      },
      {
        id: 'gen-waste-fill-reports',
        metric: 'Fill-level reports',
        meaning:
          'Count of fill-level submissions captured for reporting periods this month.',
      },
      {
        id: 'gen-waste-type-composition',
        metric: 'Waste type (composition table)',
        meaning:
          'Category label for each recycled stream row when composition detail exists.',
      },
      {
        id: 'gen-waste-total-composition',
        metric: 'Total (composition column)',
        meaning:
          'Mass attributed to each waste type in the recycled composition table.',
      },
      {
        id: 'gen-waste-materials-composition',
        metric: 'Materials (composition column)',
        meaning:
          'Nested materials inside each waste type with their masses where supplied.',
      },
    ],
  },
  {
    reportKey: 'general',
    sectionKey: 'general-utility-metrics',
    sectionTitle: 'General ESG Performance Report — Utility metrics',
    rows: [
      {
        id: 'gen-util-water',
        metric: 'Water (consumption and SAR)',
        meaning:
          'Recorded water volume or usage alongside billed SAR impact where dashboard stores both.',
      },
      {
        id: 'gen-util-electricity',
        metric: 'Electricity (kWh and SAR)',
        meaning:
          'Electricity use in kilowatt-hours with SAR cost shown on the same line.',
      },
      {
        id: 'gen-util-oil',
        metric: 'Oil (liters and SAR)',
        meaning:
          'Oil consumption volume with SAR cost captured for the reporting month.',
      },
      {
        id: 'gen-util-gas',
        metric: 'Gas (m³ and SAR)',
        meaning:
          'Gas consumption volume with SAR cost captured for the reporting month.',
      },
    ],
  },
  {
    reportKey: 'general',
    sectionKey: 'general-monitoring-metrics',
    sectionTitle: 'General ESG Performance Report — Monitoring metrics',
    rows: [
      {
        id: 'gen-mon-fill-rate',
        metric: 'Average container fill rate',
        meaning:
          'Average fullness signal for containers tracked during the month.',
      },
      {
        id: 'gen-mon-quality-compliance',
        metric: 'Quality compliance rate',
        meaning:
          'Share meeting internal quality checks where your dashboard surfaces this metric.',
      },
      {
        id: 'gen-mon-rejected-pct',
        metric: 'Rejected waste percentage',
        meaning:
          'Portion of waste rejected relative to flows recorded for the period.',
      },
      {
        id: 'gen-mon-quality-rating',
        metric: 'Waste quality rating',
        meaning:
          'Composite quality score from operational monitoring where shown.',
      },
    ],
  },
  {
    reportKey: 'general',
    sectionKey: 'general-social-training',
    sectionTitle: 'General ESG Performance Report — Social / training',
    rows: [
      {
        id: 'gen-social-training-hours',
        metric: 'Training hours (period)',
        meaning:
          'Hours logged for staff training this month; drills down elsewhere in the product.',
      },
    ],
  },
  {
    reportKey: 'general',
    sectionKey: 'general-branches',
    sectionTitle: 'General ESG Performance Report — Branches Performance',
    rows: [
      {
        id: 'gen-branch-name',
        metric: 'Branch',
        meaning:
          'Collection site or branch name tied to each monthly performance row.',
      },
      {
        id: 'gen-branch-diverted',
        metric: 'Diverted',
        meaning:
          'Mass diverted for that branch during the reporting month.',
      },
      {
        id: 'gen-branch-recycled',
        metric: 'Recycled',
        meaning:
          'Mass recycled for that branch during the reporting month.',
      },
      {
        id: 'gen-branch-diversion-rate',
        metric: 'Diversion rate',
        meaning:
          'Branch-level diversion percentage from dashboard calculations.',
      },
      {
        id: 'gen-branch-carbon',
        metric: 'Carbon avoided',
        meaning:
          'Estimated tCO2e avoided at that branch from recorded recycling activity this month.',
      },
      {
        id: 'gen-branch-fill-reports',
        metric: 'Fill reports',
        meaning:
          'Count of fill-level reports captured for that branch this period.',
      },
    ],
  },
  {
    reportKey: 'general',
    sectionKey: 'general-methodology',
    sectionTitle: 'General ESG Performance Report — Methodology',
    rows: [
      {
        id: 'gen-method-read',
        metric: 'How to read this export',
        meaning:
          'Snapshot from your monthly dashboard; figures update when underlying records change.',
      },
      {
        id: 'gen-method-compliance',
        metric: 'Compliance scope',
        meaning:
          'Does not certify GRI, ISO, or legal compliance — shows platform-recorded indicators only.',
      },
      {
        id: 'gen-method-rounding',
        metric: 'Rounding',
        meaning:
          'Displayed numbers may round; verify critical totals from source records if needed.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-exec-summary',
    sectionTitle: 'Waste & Circularity Report — Executive summary',
    rows: [
      {
        id: 'waste-exec-incoming-total',
        metric: 'Platform-recorded waste handled (incoming breakdown total)',
        meaning:
          'Same incoming-sum logic as the general report when breakdown rows exist for the month.',
      },
      {
        id: 'waste-exec-diverted',
        metric: 'Waste diverted from disposal',
        meaning:
          'Organization-level diverted mass for the month from dashboard totals.',
      },
      {
        id: 'waste-exec-diversion-rate',
        metric: 'Waste diversion rate',
        meaning:
          'Percentage diverted per dashboard calculation for the selected month.',
      },
      {
        id: 'waste-exec-recycled',
        metric: 'Waste recycled',
        meaning:
          'Recycled mass attributed to your organization for this reporting month.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-handled',
    sectionTitle: 'Waste & Circularity Report — Waste handled',
    rows: [
      {
        id: 'waste-handled-incoming-sum',
        metric: 'Incoming breakdown total (sum of amounts)',
        meaning:
          'Adds incoming recorded amounts; blank breakdown means this total cannot be inferred.',
      },
      {
        id: 'waste-handled-notes',
        metric: 'Notes (waste handled)',
        meaning:
          'Reminds readers that missing breakdown limits mass conclusions from this export.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-composition',
    sectionTitle: 'Waste & Circularity Report — Composition table',
    rows: [
      {
        id: 'waste-comp-type',
        metric: 'Waste type',
        meaning:
          'Label for each recycled stream row in the composition grid.',
      },
      {
        id: 'waste-comp-total-kg',
        metric: 'Total recycled (kg)',
        meaning:
          'Mass recycled within each waste-type row.',
      },
      {
        id: 'waste-comp-materials',
        metric: 'Materials',
        meaning:
          'Material splits nested under each waste type when detail exists.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-diverted-recycled',
    sectionTitle: 'Waste & Circularity Report — Diverted / recycled',
    rows: [
      {
        id: 'waste-div-diverted',
        metric: 'Total waste diverted',
        meaning:
          'Month-level diverted mass repeated for the circularity narrative.',
      },
      {
        id: 'waste-div-rate',
        metric: 'Waste diversion rate',
        meaning:
          'Diversion percentage paired with mass on this summary block.',
      },
      {
        id: 'waste-div-recycled',
        metric: 'Total waste recycled',
        meaning:
          'Recycled mass paired with diversion metrics for the same period.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-branch-table',
    sectionTitle: 'Waste & Circularity Report — Branch table',
    rows: [
      {
        id: 'waste-branch-name',
        metric: 'Branch',
        meaning:
          'Collection site or branch name tied to each monthly row.',
      },
      {
        id: 'waste-branch-diverted',
        metric: 'Diverted',
        meaning:
          'Mass diverted for that branch during the reporting month.',
      },
      {
        id: 'waste-branch-recycled',
        metric: 'Recycled',
        meaning:
          'Mass recycled for that branch during the reporting month.',
      },
      {
        id: 'waste-branch-diversion-rate',
        metric: 'Diversion rate',
        meaning:
          'Branch-level diversion percentage from dashboard calculations.',
      },
      {
        id: 'waste-branch-fill-reports',
        metric: 'Fill reports',
        meaning:
          'Count of fill-level reports captured for that branch this period.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-unassigned',
    sectionTitle: 'Waste & Circularity Report — Unassigned waste',
    rows: [
      {
        id: 'waste-unassigned-row',
        metric: 'Unassigned row',
        meaning:
          'Captures waste totals not mapped to a listed branch when your data includes this bucket.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-disposal-disclaimer',
    sectionTitle: 'Waste & Circularity Report — Disposal disclaimer',
    rows: [
      {
        id: 'waste-disposal-directed',
        metric: 'Waste directed to disposal',
        meaning:
          'States landfill or disposal tonnage is not exported separately; avoid guessing disposal from other columns.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-gri306-coverage',
    sectionTitle: 'Waste & Circularity Report — GRI 306 coverage rows',
    rows: [
      {
        id: 'waste-gri-generation',
        metric: 'Waste generation (mass flows)',
        meaning:
          'Compares recorded incoming flows to what full waste-generation disclosure would need.',
      },
      {
        id: 'waste-gri-diverted',
        metric: 'Waste diverted from disposal',
        meaning:
          'Links diversion metrics to disclosure expectations while noting regulatory limits.',
      },
      {
        id: 'waste-gri-circularity',
        metric: 'Circularity / recycling',
        meaning:
          'Shows whether recycled composition detail exists for circularity storytelling.',
      },
      {
        id: 'waste-gri-spatial',
        metric: 'Spatial / site disaggregation',
        meaning:
          'Checks branch-level coverage versus needs for site-by-site waste reporting.',
      },
    ],
  },
  {
    reportKey: 'waste',
    sectionKey: 'waste-methodology',
    sectionTitle: 'Waste & Circularity Report — Methodology bullets',
    rows: [
      {
        id: 'waste-method-scope',
        metric: 'Reporting scope',
        meaning:
          'Numbers honor the month filter applied in Recyclee ESG.',
      },
      {
        id: 'waste-method-branch-zeros',
        metric: 'Branch zeros',
        meaning:
          'Zero values may mean silence rather than literal zero activity.',
      },
      {
        id: 'waste-method-aligned',
        metric: '“Aligned” wording',
        meaning:
          'Indicates thematic fit with GRI 306 topics, not audited GRI statements.',
      },
    ],
  },
  {
    reportKey: 'carbon',
    sectionKey: 'carbon-exec-summary',
    sectionTitle: 'Carbon Avoidance Report — Executive summary',
    rows: [
      {
        id: 'carbon-exec-total-avoided',
        metric: 'Total avoided (tCO2e)',
        meaning:
          'Tonnes of CO2 equivalent avoided from completed cardboard and organic diversion runs in the selected date range.',
      },
      {
        id: 'carbon-exec-weight',
        metric: 'Weight diverted (tonnes)',
        meaning:
          'Total mass in tonnes associated with completed carbon calculation runs in the reporting period.',
      },
      {
        id: 'carbon-exec-completed',
        metric: 'Completed runs',
        meaning:
          'Orders where carbon was calculated successfully and included in the avoided total.',
      },
      {
        id: 'carbon-exec-skipped',
        metric: 'Skipped runs',
        meaning:
          'Orders excluded from the avoided total (unsupported material, zero weight, etc.).',
      },
    ],
  },
  {
    reportKey: 'carbon',
    sectionKey: 'carbon-breakdowns',
    sectionTitle: 'Carbon Avoidance Report — Breakdowns',
    rows: [
      {
        id: 'carbon-break-stream',
        metric: 'By stream',
        meaning:
          'Avoided tCO2e and weight grouped by material stream (cardboard or organic) with snapshotted emission factor versions.',
      },
      {
        id: 'carbon-break-material',
        metric: 'By material',
        meaning:
          'Finer breakdown by waste type and material name, each tied to the emission factor version frozen at calculation time.',
      },
      {
        id: 'carbon-break-delivery-date',
        metric: 'Delivery completion date',
        meaning:
          'Carbon figures filter by when the delivery was completed, not when the pickup order was created.',
      },
    ],
  },
  {
    reportKey: 'carbon',
    sectionKey: 'carbon-audit',
    sectionTitle: 'Carbon Avoidance Report — Audit appendix',
    rows: [
      {
        id: 'carbon-audit-formula',
        metric: 'Formula display',
        meaning:
          'Human-readable calculation for a run, e.g. weight × net EF = avoided tCO2e with the EF version used.',
      },
      {
        id: 'carbon-audit-status',
        metric: 'Run status',
        meaning:
          'Completed (counted in totals), skipped (excluded), failed (no factor or error), or pending.',
      },
      {
        id: 'carbon-audit-ef-snapshot',
        metric: 'Emission factor snapshot',
        meaning:
          'Frozen copy of the factor active on delivery completion date — never read from the live library in reports.',
      },
      {
        id: 'carbon-audit-warnings',
        metric: 'Warning codes',
        meaning:
          'Explain why a run was skipped or adjusted (unsupported material, ambiguous weight unit, etc.).',
      },
    ],
  },
  {
    reportKey: 'carbon',
    sectionKey: 'carbon-methodology',
    sectionTitle: 'Carbon Avoidance Report — Methodology',
    rows: [
      {
        id: 'carbon-method-formula',
        metric: 'Formula',
        meaning:
          'avoided_tco2e = (weight_kg ÷ 1000) × net_ef using weight from delivery and EF on completion date.',
      },
      {
        id: 'carbon-method-streams',
        metric: 'Supported streams',
        meaning:
          'Only cardboard and organic produce carbon avoidance values in this report.',
      },
      {
        id: 'carbon-method-ef-version',
        metric: 'EF versioning',
        meaning:
          'Each run stores its emission factor version at calculation time; reports always show that snapshot.',
      },
    ],
  },
];

/** Prefix used in each `sectionTitle` for the General PDF export glossary blocks. */
export const GENERAL_ESG_REPORT_GLOSSARY_TITLE_PREFIX = 'General ESG Performance Report';

/** Prefix used in each `sectionTitle` for the Waste & Circularity PDF export glossary blocks. */
export const WASTE_CIRCULARITY_REPORT_GLOSSARY_TITLE_PREFIX = 'Waste & Circularity Report';

export const GENERAL_ESG_REPORT_METRIC_GLOSSARY_SECTIONS: GlossarySection[] =
  REPORT_METRIC_GLOSSARY_SECTIONS.filter((s) => s.reportKey === 'general');

export const WASTE_CIRCULARITY_REPORT_METRIC_GLOSSARY_SECTIONS: GlossarySection[] =
  REPORT_METRIC_GLOSSARY_SECTIONS.filter((s) => s.reportKey === 'waste');

/** Prefix used in each `sectionTitle` for the Carbon Avoidance PDF export glossary blocks. */
export const CARBON_AVOIDANCE_REPORT_GLOSSARY_TITLE_PREFIX = 'Carbon Avoidance Report';

export const CARBON_AVOIDANCE_REPORT_METRIC_GLOSSARY_SECTIONS: GlossarySection[] =
  REPORT_METRIC_GLOSSARY_SECTIONS.filter((s) => s.reportKey === 'carbon');
